import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

const EDGE_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/bulk-actions`;

/**
 * Helper — call the bulk-actions edge function with auth.
 */
async function callBulkAction(body) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(EDGE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });

  const contentType = res.headers.get('content-type') || '';

  // CSV export returns text/csv
  if (contentType.includes('text/csv')) {
    const text = await res.text();
    return { csv: text, filename: `orders-${new Date().toISOString().slice(0, 10)}.csv` };
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Bulk action failed');
  return data;
}

/**
 * Bulk update products — change category, toggle featured, or delete.
 */
export function useBulkUpdateProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productIds, operation, value }) =>
      callBulkAction({
        action: 'bulk_update',
        product_ids: productIds,
        operation,
        value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Import products from CSV — supports dry_run mode.
 */
export function useImportProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ csvText, columnMap, dryRun = true }) =>
      callBulkAction({
        action: 'import_products',
        csv_text: csvText,
        column_map: columnMap,
        dry_run: dryRun,
      }),
    onSuccess: (_, variables) => {
      if (!variables.dryRun) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      }
    },
  });
}

/**
 * Export orders to CSV via edge function.
 */
export function useExportOrders() {
  return useMutation({
    mutationFn: ({ status, dateFrom, dateTo }) =>
      callBulkAction({
        action: 'export_orders',
        status: status || 'all',
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
    onSuccess: (data) => {
      if (data?.csv) {
        const blob = new Blob([data.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'orders.csv';
        a.click();
        URL.revokeObjectURL(url);
      }
    },
  });
}

/**
 * Parse CSV text client-side to extract headers for column mapping preview.
 */
export function parseCSVHeaders(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], previewRows: [] };

  const parseLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; }
          else inQuotes = false;
        } else current += ch;
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ',') { result.push(current.trim()); current = ''; }
        else current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const previewRows = lines.slice(1, 6).map((line) => {
    const vals = parseLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
    return obj;
  });

  return { headers, previewRows, totalRows: lines.length - 1 };
}

/**
 * Database fields available for mapping.
 */
export const PRODUCT_FIELDS = [
  { key: 'title', label: 'Title', required: true },
  { key: 'price', label: 'Price', required: true },
  { key: 'slug', label: 'Slug', required: false },
  { key: 'category', label: 'Category (name)', required: false },
  { key: 'short_description', label: 'Short Description', required: false },
  { key: 'description', label: 'Description', required: false },
  { key: 'is_featured', label: 'Featured', required: false },
  { key: 'image_path', label: 'Image Path', required: false },
];
